import * as React from "react";

import { pruneSynced, pruneSyncedFieldNotes } from "@/lib/outbox";

export function useStorageEstimate() {
  const [est, setEst] = React.useState<{ usage: number; quota: number; percent: number; isLow: boolean } | null>(null);

  const refresh = React.useCallback(async () => {
    if (!navigator.storage?.estimate) return;
    try {
      const raw = await navigator.storage.estimate();
      const usage = raw.usage ?? 0;
      const quota = raw.quota ?? 0;
      const percent = quota > 0 ? Math.round((usage / quota) * 100) : 0;
      const isLow = quota > 0 && (percent > 70 || (quota - usage) < 10 * 1024 * 1024);
      setEst({ usage, quota, percent, isLow });

      // Auto-prune old synced data if storage is getting low (non-blocking). The explicit header button does a full clear of all synced (via confirmation).
      if (isLow) {
        void Promise.all([pruneSynced(), pruneSyncedFieldNotes()]).catch(() => {});
      }
    } catch {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    refresh();
    const id = setInterval(refresh, 120000); // gentle poll (was 45s; reduced to avoid unnecessary work/delays)
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh]);

  return { est, refresh };
}
